import { useState } from 'react';
import RichTextEditor from './RichTextEditor';

const categories = ['Family Law', 'Criminal Law', 'Civil Law', 'Corporate Law', 'Other'];

const BlogEditor = ({ onSave, initial = {} }) => {
  const [title, setTitle] = useState(initial.title || '');
  const [slug, setSlug] = useState(initial.slug || '');
  const [excerpt, setExcerpt] = useState(initial.excerpt || '');
  const [content, setContent] = useState(initial.content || '');
  const [category, setCategory] = useState(initial.category || 'Family Law');
  const [metaDesc, setMetaDesc] = useState(initial.metaDesc || '');
  const [keywords, setKeywords] = useState(initial.keywords || '');
  const [featuredImage, setFeaturedImage] = useState(initial.featuredImage || '');
  const [tags, setTags] = useState(initial.tags || '');

  // Auto-generate slug from title
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    if (!slug) {
      setSlug(generateSlug(value));
    }
  };

  const handleSave = () => {
    if (!title || !content) {
      alert('Title and content are required');
      return;
    }

    onSave({
      title,
      slug: slug || generateSlug(title),
      excerpt,
      content,
      category,
      metaDesc,
      keywords,
      featuredImage,
      tags,
    });
  };

  const handlePublish = () => {
    if (!title || !content) {
      alert('Title and content are required');
      return;
    }
    onSave({
      title,
      slug: slug || generateSlug(title),
      excerpt,
      content,
      category,
      metaDesc,
      keywords,
      featuredImage,
      tags,
    }, { published: true });
  };

  const handlePreview = () => {
    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; color:#111827; padding:40px; }
          h1{ font-size:32px; margin-bottom:10px }
          .meta{ color:#6b7280; margin-bottom:20px }
          img{ max-width:100%; height:auto }
          .content p{ font-size:16px; line-height:1.7 }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div class="meta">${category} • ${new Date().toLocaleDateString()}</div>
        <div class="content">${content}</div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank');
    if (!w) {
      alert('Popup blocked. Allow popups or use the Print preview in your browser.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Give browser a moment to render then call print
    setTimeout(() => {
      try { w.print(); } catch (e) { /* ignore */ }
    }, 500);
  };

  return (
    <div className="space-y-6 bg-gray-50 p-6 rounded-lg">
      {/* Title */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Article Title *</label>
        <input
          className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          value={title}
          onChange={handleTitleChange}
          placeholder="e.g., Understanding Family Law Basics"
        />
      </div>

      {/* Slug */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">URL Slug</label>
        <input
          className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm bg-gray-100"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="auto-generated-from-title"
        />
        <p className="text-xs text-gray-500 mt-1">URL: /blog/{slug || 'your-slug-here'}</p>
      </div>

      {/* Category & Featured Image */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Featured Image URL</label>
          <input
            className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
            value={featuredImage}
            onChange={(e) => setFeaturedImage(e.target.value)}
            placeholder="https://example.com/image.jpg"
          />
        </div>
      </div>

      {/* Excerpt */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Excerpt (Short Summary)</label>
        <textarea
          className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          rows={3}
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="Brief summary of the article (shown in listings)"
        />
        <p className="text-xs text-gray-500 mt-1">{excerpt.length} / 160 characters</p>
      </div>

      {/* Content */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Article Content *</label>
        <RichTextEditor value={content} onChange={setContent} />
      </div>

      {/* SEO Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-4">SEO Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Description</label>
            <textarea
              className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
              rows={2}
              value={metaDesc}
              onChange={(e) => setMetaDesc(e.target.value)}
              placeholder="Brief description for search engines (50-160 chars)"
            />
            <p className="text-xs text-gray-500 mt-1">{metaDesc.length} / 160 characters</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Keywords (Comma-separated)</label>
            <input
              className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="family law, divorce, custody, rights"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Tags (Comma-separated)</label>
            <input
              className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="guides, tips, legal-advice"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 items-center">
        <button
          onClick={handleSave}
          className="bg-gray-500 text-white font-semibold px-4 py-2 rounded hover:bg-gray-600 transition"
          title="Save as draft"
        >
          Save Draft
        </button>

        <button
          onClick={handlePublish}
          className="bg-green-600 text-white font-semibold px-4 py-2 rounded hover:bg-green-700 transition"
          title="Publish article"
        >
          Publish Article
        </button>

        <button
          onClick={handlePreview}
          className="ml-auto bg-indigo-600 text-white font-semibold px-4 py-2 rounded hover:bg-indigo-700 transition"
          title="Preview as PDF/print"
        >
          Preview / Print
        </button>
      </div>
    </div>
  );
};

export default BlogEditor;
